import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { useGetAgreementImportsBySessionIdContractsQuery } from '@client/api';
import { Button, Card, CardContent, CardHeader, CardTitle, Typography } from '@client/toolkit';

import { PortfolioPaths } from '../../../../../portfolio.router';

interface StepDoneProps {
  sessionId: string;
  onImportAnother: () => void;
  onBack: () => void;
}

/**
 * Step 6 — session summary. <c>ContractReview</c> doesn't expose the created
 * <c>AgreementId</c>, so this links to the building's agreements list rather than
 * individual draft agreements.
 */
export function StepDone({ sessionId, onImportAnother, onBack }: StepDoneProps) {
  const { t: tAgreements } = useTranslation('agreements');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const { buildingId } = useParams<{ buildingId: string }>();

  const { data: contracts } = useGetAgreementImportsBySessionIdContractsQuery({ sessionId });
  const items = useMemo(() => contracts ?? [], [contracts]);
  const approvedCount = useMemo(() => items.filter((c) => c.status === 'Approved').length, [items]);

  const handleClose = () => {
    navigate(generatePath(PortfolioPaths.BuildingAgreements, { buildingId: buildingId ?? '' }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle variant="highlighted">{tAgreements('agreementImport.done.title')}</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        <Typography variant="secondary">
          {tAgreements('agreementImport.done.description', { approved: approvedCount, total: items.length })}
        </Typography>

        <div className="flex justify-between gap-2">
          <Button color="primary-dark" onClick={onBack}>
            {tCommon('back')}
          </Button>
          <div className="flex gap-2">
            <Button color="primary-invert" onClick={onImportAnother}>
              {tAgreements('agreementImport.done.importAnother')}
            </Button>
            <Button color="primary" onClick={handleClose}>
              {tAgreements('agreementImport.done.close')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
